// src/toolkitServices/stoneApi.js
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

/* ---------------- helpers (filter matcher + list patchers) ---------------- */
const str = (x) => (x == null ? "" : String(x));
const sameId = (a, b) => String(a || "") === String(b || "");

/**
 * Matches a stone against list filters. Keep this lenient:
 * - supports either raw ObjectId or populated objects
 * - search checks common text fields
 */
const matchesFilters = (item, args = {}) => {
  const {
    search = "",
    isActive = "",         // "true" | "false" | ""
    unit = "",             // "gram" | "carat" | ""
    deductable = "",       // "yes" | "no" | ""  (UI uses yes/no)
    shape = "",            // id
    color = "",            // id
    size = "",             // id
    settingType = "",      // id
    dateFrom = "",         // ISO string (inclusive)
    dateTo = "",           // ISO string (inclusive)
  } = args || {};

  // Booleans / enums
  if (isActive !== "") {
    const want = String(isActive) === "true";
    if (Boolean(item.isActive) !== want) return false;
  }
  if (unit && str(item.unit).toLowerCase() !== str(unit).toLowerCase()) return false;
  if (deductable !== "") {
    const want = String(deductable).toLowerCase() === "yes";
    if (Boolean(item.deductable) !== want) return false;
  }

  // Attribute ids (server may return id or populated object)
  const getId = (v) => (v && typeof v === "object" ? v._id || v.id || v.value : v);
  if (shape && !sameId(getId(item.shape), shape)) return false;
  if (color && !sameId(getId(item.color), color)) return false;
  if (size && !sameId(getId(item.size), size)) return false;
  if (settingType && !sameId(getId(item.settingType), settingType)) return false;

  // Dates
  if (dateFrom && new Date(item.createdAt) < new Date(dateFrom)) return false;
  if (dateTo && new Date(item.createdAt) > new Date(dateTo)) return false;

  // Search
  if (search) {
    const q = search.toLowerCase();
    const hay = [
      item.stoneName,
      item.stnPopupDescription,
      // if server populates names, include them
      item.shapeName,
      item.colorName,
      item.sizeName,
      item.settingTypeName,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    if (!hay.includes(q)) return false;
  }

  return true;
};

// patch list helper (works with {total,pages,limit} and with {pagination:{...}})
const addItemsIntoListDraft = (draft, args, newItems = []) => {
  if (!Array.isArray(draft?.data)) return;
  // Only inject into page 1 to avoid paging anomalies
  if (args.page && Number(args.page) !== 1) {
    incrementTotals(draft, newItems.length);
    return;
  }
  draft.data.unshift(...newItems);
  if (args.limit) draft.data.splice(Number(args.limit)); // clamp
  incrementTotals(draft, newItems.length);
};

const incrementTotals = (draft, by = 1) => {
  if (draft?.pagination?.totalDocs != null) {
    draft.pagination.totalDocs += by;
    const lim = draft?.pagination?.limit || draft?.limit || 10;
    draft.pagination.totalPages = Math.max(1, Math.ceil(draft.pagination.totalDocs / lim));
  } else if (draft?.total != null) {
    draft.total += by;
    const lim = draft?.limit || draft?.pagination?.limit || 10;
    draft.pages = Math.max(1, Math.ceil(draft.total / lim));
  }
};

const decrementTotals = (draft, by = 1) => {
  if (draft?.pagination?.totalDocs != null) {
    draft.pagination.totalDocs = Math.max(0, draft.pagination.totalDocs - by);
    const lim = draft?.pagination?.limit || draft?.limit || 10;
    draft.pagination.totalPages = Math.max(1, Math.ceil(draft.pagination.totalDocs / lim));
  } else if (draft?.total != null) {
    draft.total = Math.max(0, draft.total - by);
    const lim = draft?.limit || draft?.pagination?.limit || 10;
    draft.pages = Math.max(1, Math.ceil(draft.total / lim));
  }
};

/* ---------------------------------- API ---------------------------------- */
export const stoneApi = createApi({
  reducerPath: "stoneApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE_URL, // e.g. https://server/api
    credentials: "include",
  }),
  tagTypes: ["Stone"], // present but we're doing manual patching
  endpoints: (builder) => ({
    /* ---------------- LIST (filters) ---------------- */
    // GET /stones?page=&limit=&q=&isActive=&unit=&deductable=&shape=&color=&size=&settingType=&dateFrom=&dateTo=
    getAllStones: builder.query({
      query: (args = {}) => {
        const {
          page = 1,
          limit = 10,
          search = "",
          isActive = "",
          unit = "",
          deductable = "",
          shape = "",
          color = "",
          size = "",
          settingType = "",
          dateFrom = "",
          dateTo = "",
          sortBy,
          sortOrder,
        } = args;

        const params = { page, limit, q: search };
        if (isActive !== "") params.isActive = isActive;
        if (unit) params.unit = unit;
        if (deductable !== "") params.deductable = deductable;
        if (shape) params.shape = shape;
        if (color) params.color = color;
        if (size) params.size = size;
        if (settingType) params.settingType = settingType;
        if (dateFrom) params.dateFrom = dateFrom;
        if (dateTo) params.dateTo = dateTo;
        if (sortBy) params.sortBy = sortBy;
        if (sortOrder) params.sortOrder = sortOrder;

        return { url: "/stones", method: "GET", params };
      },
      providesTags: ["Stone"],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const a = queryArgs || {};
        return [
          endpointName,
          a.page,
          a.limit,
          a.search,
          a.isActive,
          a.unit,
          a.deductable,
          a.shape,
          a.color,
          a.size,
          a.settingType,
          a.dateFrom,
          a.dateTo,
          a.sortBy,
          a.sortOrder,
        ].join("|");
      },
      keepUnusedDataFor: 60,
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    }),

    /* ---------------- GET ONE ---------------- */
    getStoneById: builder.query({
      query: (id) => `/stones/${id}`,
      providesTags: (_res, _err, id) => [{ type: "Stone", id }],
      keepUnusedDataFor: 120,
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    }),

    /* ---------------- CREATE ---------------- */
    // POST /stones/create
    addStone: builder.mutation({
      query: (body) => ({
        url: `/stones/create`,
        method: "POST",
        body,
      }),
      // No invalidates — we patch caches below
      async onQueryStarted(payload, { dispatch, queryFulfilled, getState }) {
        try {
          const { data: res } = await queryFulfilled;
          const created = res?.data ? (Array.isArray(res.data) ? res.data : [res.data]) : [];
          if (!created.length) return;

          // inject into every cached list whose filters match
          const allArgs = stoneApi.util.selectCachedArgsForQuery(getState(), "getAllStones");
          for (const args of allArgs) {
            const addable = created.filter((it) => matchesFilters(it, args));
            if (!addable.length) continue;

            dispatch(
              stoneApi.util.updateQueryData("getAllStones", args, (draft) => {
                addItemsIntoListDraft(draft, args, addable);
              })
            );
          }
        } catch {
          // handled by component toasts
        }
      },
    }),

    /* ---------------- UPDATE ---------------- */
    // PUT /stones/:id
    updateStone: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/stones/${id}`,
        method: "PUT",
        body,
      }),
      async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled, getState }) {
        const patches = [];

        // patch across all cached lists
        const allArgs = stoneApi.util.selectCachedArgsForQuery(getState(), "getAllStones");
        for (const args of allArgs) {
          patches.push(
            dispatch(
              stoneApi.util.updateQueryData("getAllStones", args, (draft) => {
                if (!Array.isArray(draft?.data)) return;
                const idx = draft.data.findIndex((it) => String(it._id) === String(id));
                if (idx >= 0) {
                  // apply patch; if it stops matching filters, drop it
                  const next = { ...draft.data[idx], ...patch };
                  if (matchesFilters(next, args)) {
                    draft.data[idx] = next;
                  } else {
                    draft.data.splice(idx, 1);
                    decrementTotals(draft, 1);
                  }
                } else {
                  // if now matches and page=1, we can optionally inject (rare)
                  const phantom = { _id: id, ...patch };
                  if ((args.page ?? 1) === 1 && matchesFilters(phantom, args)) {
                    draft.data.unshift(phantom);
                    incrementTotals(draft, 1);
                    if (args.limit) draft.data.splice(Number(args.limit));
                  }
                }
              })
            )
          );
        }

        // also patch detail cache
        patches.push(
          dispatch(
            stoneApi.util.updateQueryData("getStoneById", id, (draft) => {
              if (draft?.data) Object.assign(draft.data, patch);
            })
          )
        );

        try {
          const { data: res } = await queryFulfilled;
          const updated = res?.data;

          if (updated) {
            for (const args of allArgs) {
              dispatch(
                stoneApi.util.updateQueryData("getAllStones", args, (draft) => {
                  if (!Array.isArray(draft?.data)) return;
                  const idx = draft.data.findIndex((it) => String(it._id) === String(id));
                  // replace with server truth or drop if it no longer matches
                  if (idx >= 0) {
                    if (matchesFilters(updated, args)) {
                      draft.data[idx] = { ...draft.data[idx], ...updated };
                    } else {
                      draft.data.splice(idx, 1);
                      decrementTotals(draft, 1);
                    }
                  } else if ((args.page ?? 1) === 1 && matchesFilters(updated, args)) {
                    draft.data.unshift(updated);
                    incrementTotals(draft, 1);
                    if (args.limit) draft.data.splice(Number(args.limit));
                  }
                })
              );
            }
            dispatch(
              stoneApi.util.updateQueryData("getStoneById", id, (draft) => {
                if (draft?.data) Object.assign(draft.data, updated);
              })
            );
          }
        } catch {
          patches.forEach((p) => p?.undo && p.undo());
        }
      },
    }),

    /* ---------------- DELETE ---------------- */
    // DELETE /stones/:id
    deleteStone: builder.mutation({
      query: (id) => ({ url: `/stones/${id}`, method: "DELETE" }),
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        const patches = [];
        const allArgs = stoneApi.util.selectCachedArgsForQuery(getState(), "getAllStones");
        for (const args of allArgs) {
          patches.push(
            dispatch(
              stoneApi.util.updateQueryData("getAllStones", args, (draft) => {
                if (!Array.isArray(draft?.data)) return;
                const before = draft.data.length;
                draft.data = draft.data.filter((it) => String(it._id) !== String(id));
                const removed = before - draft.data.length;
                if (removed > 0) decrementTotals(draft, removed);
              })
            )
          );
        }
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p?.undo && p.undo());
        }
      },
    }),

    /* ---------------- ACTIVATE / DEACTIVATE ---------------- */
    // PATCH /stones/:id/activate
    activateStone: builder.mutation({
      query: (id) => ({ url: `/stones/${id}/activate`, method: "PATCH" }),
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        const patches = [];
        const allArgs = stoneApi.util.selectCachedArgsForQuery(getState(), "getAllStones");
        for (const args of allArgs) {
          patches.push(
            dispatch(
              stoneApi.util.updateQueryData("getAllStones", args, (draft) => {
                const it = draft?.data?.find?.((x) => String(x._id) === String(id));
                if (it) it.isActive = true;
              })
            )
          );
        }
        patches.push(
          dispatch(
            stoneApi.util.updateQueryData("getStoneById", id, (draft) => {
              if (draft?.data) draft.data.isActive = true;
            })
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p?.undo && p.undo());
        }
      },
    }),

    // PATCH /stones/:id/deactivate
    deactivateStone: builder.mutation({
      query: (id) => ({ url: `/stones/${id}/deactivate`, method: "PATCH" }),
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        const patches = [];
        const allArgs = stoneApi.util.selectCachedArgsForQuery(getState(), "getAllStones");
        for (const args of allArgs) {
          patches.push(
            dispatch(
              stoneApi.util.updateQueryData("getAllStones", args, (draft) => {
                const it = draft?.data?.find?.((x) => String(x._id) === String(id));
                if (it) it.isActive = false;
              })
            )
          );
        }
        patches.push(
          dispatch(
            stoneApi.util.updateQueryData("getStoneById", id, (draft) => {
              if (draft?.data) draft.data.isActive = false;
            })
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p?.undo && p.undo());
        }
      },
    }),
  }),
});

export const {
  useGetAllStonesQuery,
  useGetStoneByIdQuery,
  useAddStoneMutation,
  useUpdateStoneMutation,
  useDeleteStoneMutation,
  useActivateStoneMutation,
  useDeactivateStoneMutation,
} = stoneApi;

export default stoneApi;
